import React, { useState, useCallback, useRef, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Button } from "../design/button";
import {
  PhotoIcon,
  XMarkIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/24/outline";
import { Text } from "../design/text";
import clsx from "clsx";

interface LogoUploadProps {
  currentLogoUrl?: string | null;
  onFileSelected: (file: File | null) => void;
  disabled?: boolean;
  className?: string;
}

const LogoUpload: React.FC<LogoUploadProps> = ({
  currentLogoUrl,
  onFileSelected,
  disabled = false,
  className,
}) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        // Revoke previous preview URL if exists
        if (preview) {
          URL.revokeObjectURL(preview);
        }
        const newPreview = URL.createObjectURL(file);
        setPreview(newPreview);
        onFileSelected(file);
      }
    },
    [preview, onFileSelected]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp", ".svg", ".avif"],
    },
    maxFiles: 1,
    disabled,
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
  });

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (preview) {
        URL.revokeObjectURL(preview);
      }
      setPreview(null);
      onFileSelected(null);
    },
    [preview, onFileSelected]
  );

  const displayImage = preview || currentLogoUrl;
  const hasNewImage = !!preview;
  const hasExistingImage = !!currentLogoUrl && !preview;

  return (
    <div className={clsx("w-full", className)}>
      <div
        {...getRootProps()}
        className={clsx(
          "relative group cursor-pointer rounded-xl border-2 border-dashed transition-all duration-200",
          "flex flex-col items-center justify-center",
          isDragActive || isDragging
            ? "border-blue-400 bg-blue-50 dark:bg-blue-900/20"
            : "border-zinc-300 dark:border-zinc-600 hover:border-zinc-400 dark:hover:border-zinc-500",
          displayImage
            ? "p-4"
            : "p-8",
          disabled && "opacity-60 cursor-not-allowed"
        )}
      >
        <input {...getInputProps()} ref={fileInputRef} />

        {displayImage ? (
          // Show logo preview
          <div className="relative w-full">
            <div className="flex items-center justify-center">
              <img
                src={displayImage}
                alt="Business logo"
                className="max-h-32 max-w-full object-contain rounded-lg"
              />
            </div>

            {/* Overlay on hover */}
            {!disabled && (
              <div
                className={clsx(
                  "absolute inset-0 flex items-center justify-center rounded-lg transition-opacity duration-200",
                  "bg-black/50",
                  isDragActive
                    ? "opacity-100"
                    : "opacity-0 group-hover:opacity-100"
                )}
              >
                <div className="flex flex-col items-center gap-2 text-white">
                  <ArrowUpTrayIcon className="w-8 h-8" />
                  <span className="text-sm font-medium">
                    {hasExistingImage ? "Replace logo" : "Change logo"}
                  </span>
                </div>
              </div>
            )}

            {/* Badge for new image */}
            {hasNewImage && !disabled && (
              <div className="absolute -top-2 -right-2 flex items-center gap-1 px-2 py-1 bg-green-500 rounded-full text-white text-xs font-medium">
                <ArrowPathIcon className="w-3 h-3" />
                New
              </div>
            )}
          </div>
        ) : (
          // Empty state
          <div className="flex flex-col items-center gap-3 text-center">
            <div
              className={clsx(
                "p-4 rounded-full transition-colors",
                isDragActive
                  ? "bg-blue-100 dark:bg-blue-900/40"
                  : "bg-zinc-100 dark:bg-zinc-800"
              )}
            >
              <PhotoIcon
                className={clsx(
                  "w-10 h-10 transition-colors",
                  isDragActive
                    ? "text-blue-600 dark:text-blue-400"
                    : "text-zinc-400 dark:text-zinc-500"
                )}
              />
            </div>
            <div>
              <Text className="font-medium text-zinc-900 dark:text-zinc-100">
                {isDragActive ? "Drop your logo here" : "Upload your business logo"}
              </Text>
              <Text className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                Drag and drop or click to browse
              </Text>
            </div>
            <Text className="text-xs text-zinc-400 dark:text-zinc-500">
              PNG, JPG, SVG, or WebP (max 5MB)
            </Text>
          </div>
        )}
      </div>

      {/* Action buttons below the dropzone */}
      {displayImage && !disabled && (
        <div className="flex items-center justify-center gap-3 mt-3">
          <Button
            type="button"
            outline
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              fileInputRef.current?.click();
            }}
            className="text-sm"
          >
            <ArrowUpTrayIcon className="w-4 h-4 mr-1.5" />
            {hasExistingImage ? "Replace" : "Change"}
          </Button>
          {hasNewImage && (
            <Button
              type="button"
              plain
              onClick={handleRemove}
              className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
            >
              <XMarkIcon className="w-4 h-4 mr-1" />
              Remove
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default LogoUpload;
